import { EnvironmentProviders, Provider, inject, provideAppInitializer } from '@angular/core';
import { TitleStrategy } from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { LanguageService } from '../../services/language.service';
import { DEFAULT_LANGUAGE } from './language';
import { TranslatedTitleStrategy } from './translated-title.strategy';

/**
 * Where the dictionaries are served from — `public/i18n/*.json`.
 *
 * Relative, so an installation served from a sub-path resolves them against its
 * own `<base href>` instead of the domain root.
 */
const TRANSLATION_PREFIX = 'i18n/';
const TRANSLATION_SUFFIX = '.json';

/**
 * Wires up runtime translations.
 *
 * The dictionaries are fetched over `HttpBackend` rather than `HttpClient`, so
 * application interceptors (API base URL, auth token) never touch them — they
 * are static assets, not backend calls.
 *
 * An app initializer loads the resolved language before the first render, so
 * the UI never flashes raw translation keys.
 */
export function provideI18n(): (Provider | EnvironmentProviders)[] {
  return [
    provideTranslateService({ fallbackLang: DEFAULT_LANGUAGE }),
    provideTranslateHttpLoader({
      prefix: TRANSLATION_PREFIX,
      suffix: TRANSLATION_SUFFIX,
      useHttpBackend: true,
    }),
    provideAppInitializer(() => inject(LanguageService).initialize()),

    { provide: TitleStrategy, useExisting: TranslatedTitleStrategy },
  ];
}
